import { FormattedMessage, useIntl } from 'react-intl';

import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import {
  ApplyButtonStateProvider,
  useShowApplyButton,
} from '@xing-com/crate-jobs-components-apply-button';
import { AriaExtendedText } from '@xing-com/crate-jobs-components-aria-extended-text';
import { BannerContextProvider } from '@xing-com/crate-jobs-components-banner';
import { XxsGapCol } from '@xing-com/crate-jobs-components-columns';
import {
  JobTeaserFacts,
  useAriaLabel,
} from '@xing-com/crate-jobs-components-job-teaser-options';
import { MultiLocationDisplay } from '@xing-com/crate-jobs-components-multi-location-display';
import type { ApplicationData } from '@xing-com/crate-jobs-helpers';
import { selectJobData } from '@xing-com/crate-jobs-helpers';
import {
  useJobBookmarkToggle,
  useJobTeaserClickExternalTracking,
} from '@xing-com/crate-jobs-hooks';
import type { JobTeaserCardProps } from '@xing-com/crate-jobs-xinglets';
import {
  IconBookmark,
  IconBookmarkFilled,
  IconStarFilled,
} from '@xing-com/icons';

import * as S from './job-teaser-card.styles';
import { BookMarkButton } from './job-teaser-card.styles';

export const JobTeaserCard: React.FC<JobTeaserCardProps> = ({
  'aria-label': ariaLabel,
  className,
  'data-testid': dataTestId,
  hasTopButton = false,
  headingLevel = 'h3',
  isNew,
  job,
  matchingHighlights,
  nwtTrackingOptionsBookmarkToggle,
  onAuxClick,
  onClick: clickCallback,
  onContextMenu,
  profileImageSize = 'xsmall',
  profileImageSizeConfined = 'xsmall',
  target = '_blank',
  url,
  variant = 'default',
  hideApplyButton,
  onBookmark: onBookmarkProp,
}) => {
  const { isLoggedIn } = useLoginState();
  const { formatMessage } = useIntl();
  const {
    activeUntil,
    id,
    refreshedAt,
    title,
    locations,
    companyName,
    companyLogo,
    redirectsToThirdPartyUrl,
    applyUrl,
    isBookmarked,
  } = selectJobData(job);
  const onBookmark = useJobBookmarkToggle({
    jobId: job.id,
    isBookmarked,
    nwtTrackingOptions: nwtTrackingOptionsBookmarkToggle ?? {
      eventSchema: 'basic',
    },
    onCompleted: () => {},
    onError: () => {},
  });
  const isCompacted = variant === 'compact';

  const ariaLabelText = useAriaLabel({
    ariaLabel: ariaLabel ?? title,
    redirectsToThirdPartyUrl,
    target,
  });

  const onClick = useJobTeaserClickExternalTracking({
    isExternalJob: redirectsToThirdPartyUrl,
    jobId: id,
    url,
    clickCallback,
  });

  const handleOnBookmark = (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ): void => {
    e.preventDefault();

    if (onBookmarkProp) {
      onBookmarkProp();
    } else {
      onBookmark();
    }
  };

  const isLastChangeHighlight =
    matchingHighlights?.highlight?.type === 'job_highlight_last_chance';
  const isSuperMatch =
    isLoggedIn &&
    job.userProfileMatches?.skills?.matchedSkills &&
    job.userProfileMatches.skills.matchedSkills.length > 3 &&
    job.paid;

  const applicationData: ApplicationData | undefined = job.application
    ? {
        applyUrl:
          job.application.__typename === 'UrlApplication'
            ? (job.application.applyUrl ?? undefined)
            : undefined,
        jobId: job.id,
        paid: job.paid,
        prioritized: false,
        slug: job.slug,
        title: job.title,
        type: job.application.__typename,
      }
    : undefined;
  const { showApplyButton } = useShowApplyButton({
    job,
  });
  const allApplyButtonRequirements =
    showApplyButton && !hideApplyButton && isLoggedIn && !!applicationData;

  return (
    <S.Card
      className={className}
      forwardedAs="article"
      href={redirectsToThirdPartyUrl && applyUrl ? applyUrl : url}
      linkProps={{
        'aria-label': ariaLabelText,
        onAuxClick,
        onClick,
        onContextMenu,
        rel: redirectsToThirdPartyUrl
          ? 'noopener nofollow sponsored'
          : undefined,
        target,
      }}
      data-testid={dataTestId}
      $isCompacted={isCompacted}
    >
      <S.HeaderContainer $isCompacted={isCompacted}>
        <S.TopRow $isCompacted={isCompacted}>
          <S.TopRowGroup $isCompacted={isCompacted}>
            <S.Image
              type="company"
              profileName={companyName}
              size={profileImageSize}
              sizeConfined={profileImageSizeConfined}
              src={companyLogo ?? ''}
              // @ts-expect-error FIXME: SC6
              title={null}
              alt=""
              aria-hidden
            />
            {redirectsToThirdPartyUrl ? (
              <S.TopHighlight variant="default" $hasTopButton={hasTopButton}>
                <AriaExtendedText
                  item={{ id: 'JOBS_JOB_TEASER_EXTERNAL_JOB_HIGHLIGHT' }}
                  a11yItem={{
                    id: 'JOBS_JOB_TEASER_EXTERNAL_JOB_HIGHLIGHT_A11Y',
                  }}
                />
              </S.TopHighlight>
            ) : isSuperMatch && !isLastChangeHighlight ? (
              <S.TopHighlight
                variant="strong"
                $hasTopButton={hasTopButton}
                icon={IconStarFilled}
              >
                <FormattedMessage id="JOBS_JOB_DETAILS_SKILLS_SUPER_MATCH_HEADLINE" />
              </S.TopHighlight>
            ) : matchingHighlights?.highlight ? (
              <S.TopHighlight variant="highlight" $hasTopButton={hasTopButton}>
                {matchingHighlights?.highlight?.localization.localizationValue}
              </S.TopHighlight>
            ) : null}
          </S.TopRowGroup>
          <BookMarkButton
            aria-label={
              isBookmarked
                ? formatMessage({ id: 'JOBS_JOB_TEASER_SAVED' })
                : formatMessage({ id: 'JOBS_JOB_TEASER_SAVE' })
            }
            fontWeight="regular"
            icon={isBookmarked ? IconBookmarkFilled : IconBookmark}
            onClick={handleOnBookmark}
            size="small"
            $isCompacted={isCompacted}
          />
        </S.TopRow>
        <S.Details>
          <XxsGapCol>
            <S.Title
              forwardedAs={headingLevel}
              size={isCompacted ? 'small' : 'medium'}
              data-testid="job-teaser-card-title"
              noMargin
              $isCompacted={isCompacted}
            >
              {title}
            </S.Title>
            <MultiLocationDisplay lineHeight="dense" locations={locations} />
            <S.Company
              data-testid="job-teaser-card-company"
              size={isCompacted ? 'small' : 'medium'}
              lineHeight="dense"
              noMargin
              $isCompacted={isCompacted}
            >
              {companyName}
            </S.Company>
          </XxsGapCol>
          <JobTeaserFacts
            isCompacted={isCompacted}
            job={job}
            matchingHighlights={matchingHighlights}
            applyButtonVisible={allApplyButtonRequirements}
          />
        </S.Details>
        {isCompacted ? null : refreshedAt ? (
          <S.EndRow>
            <S.PublicationDate
              activeUntil={activeUntil}
              date={refreshedAt}
              isNew={isNew}
            />
          </S.EndRow>
        ) : null}
      </S.HeaderContainer>
      <BannerContextProvider disableFocusTrap>
        <ApplyButtonStateProvider
          jobSlug={job.slug}
          jobId={job.id}
          applicationType={job.application?.__typename}
        >
          {allApplyButtonRequirements ? (
            <S.IconOnlyApplyButton
              application={applicationData}
              variant="primary"
              iconOnly
            />
          ) : null}
        </ApplyButtonStateProvider>
      </BannerContextProvider>
    </S.Card>
  );
};
